import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { create } from "zustand";
import { createChatHistorySlice } from "@/zapi-frontend/stores/lib/createChatHistorySlice";
import { persist } from "zustand/middleware";
import $api from "@/zapi-frontend/shared/api/axios";
import { AxiosError } from "axios";
import { confirmPollingEnd } from "@/zapi-frontend/shared/lib/confirmPollingEnd";
import { EGenerationStatus, EGenerationTaskType } from "@/zapi-frontend/widgets/ChatIsland/types";
import { getStatusAll } from "@/zapi-frontend/widgets/ChatIsland";
import { usePresentationStore, TSlide, type TPresentation } from "@/zapi-frontend/stores/presentation";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { toast } from "sonner";
import { LS_PRESENTATION_CHAT_STORAGE } from "@/zapi-frontend/shared/local-storage/keys";

export type PresentationChatMessage = {
  role: "user" | "assistant";
  content: string;
  id: string;
  isActedOn?: boolean;
};

type PresentationChatHistory = {
  [projectId: string]: PresentationChatMessage[];
};

type PresentationChatLoadingState = {
  loadingMessageId: string | null;
  pendingBotMessage: PresentationChatMessage | null;
  pendingGenerationId: number | null;
  generationStep: number | null;
};

type PresentationChatLoadingStates = {
  [projectId: string]: PresentationChatLoadingState;
};

interface PresentationChatState {
  chatHistory: PresentationChatHistory;
  loadingStates: PresentationChatLoadingStates;
  changesDismissedAt: { [projectId: string]: string | null };
  getChatHistory: (projectId: string) => PresentationChatMessage[];
  addMessage: (projectId: string, message: PresentationChatMessage) => void;
  clearChatHistory: (projectId: string) => void;
  removeLastMessage: (projectId: string) => void;
  removeMessage: (projectId: string, messageId: string) => void;
  markMessageActedOn: (projectId: string, messageId: string) => void;
  getLoadingState: (projectId: string) => PresentationChatLoadingState;
  setLoadingState: (projectId: string, updates: Partial<PresentationChatLoadingState>) => void;
  resetLoadingState: (projectId: string) => void;
  getMessagesWithPending: (projectId: string) => PresentationChatMessage[];
  setChangesDismissedAt: (projectId: string, value: string) => void;
}

const createDefaultLoadingState = (): PresentationChatLoadingState => ({
  loadingMessageId: null,
  pendingBotMessage: null,
  pendingGenerationId: null,
  generationStep: null
});

const STEP_MAP: Record<string, number> = { planning: 1, generating: 2 };

function parseGenerationStep(result: string | null | undefined): number {
  if (!result) return 0;
  try {
    const parsed = JSON.parse(result);
    return STEP_MAP[parsed.step] ?? 0;
  } catch {
    return 0;
  }
}

export const usePresentationChatStore = create<PresentationChatState>()(
  persist(
    (set, get) => {
      const slice = createChatHistorySlice<PresentationChatMessage, PresentationChatLoadingState>(
        set,
        get,
        createDefaultLoadingState
      );

      return {
        ...slice,
        changesDismissedAt: {},

        clearChatHistory: (projectId: string) => {
          slice.clearChatHistory(projectId);
          slice.resetLoadingState(projectId);
        },

        removeMessage: (projectId: string, messageId: string) => {
          set((state) => {
            const messages = state.chatHistory[projectId] || [];
            return {
              chatHistory: {
                ...state.chatHistory,
                [projectId]: messages.filter((m) => m.id !== messageId)
              }
            };
          });
        },

        markMessageActedOn: (projectId: string, messageId: string) => {
          set((state) => {
            const messages = state.chatHistory[projectId] || [];
            return {
              chatHistory: {
                ...state.chatHistory,
                [projectId]: messages.map((m) => (m.id === messageId ? { ...m, isActedOn: true } : m))
              }
            };
          });
        },

        setChangesDismissedAt: (projectId: string, value: string) => {
          set((state) => ({
            changesDismissedAt: { ...state.changesDismissedAt, [projectId]: value }
          }));
        }
      };
    },
    {
      name: LS_PRESENTATION_CHAT_STORAGE,
      skipHydration: true,
      partialize: (state) => ({
        chatHistory: state.chatHistory,
        loadingStates: state.loadingStates,
        changesDismissedAt: state.changesDismissedAt
      })
    }
  )
);

const MAX_CHAT_HISTORY_LENGTH = 20;

function getLastItems<T>(arr: T[], n: number): T[] {
  return arr.slice(-n);
}

type SlidesResult = {
  message: string;
  slidesCount?: number;
  slides: Array<{ id: number; title: string; content: string; order: number }>;
};

function parseSlidesResult(result: string | null | undefined): SlidesResult | null {
  if (!result) return null;
  try {
    const parsed = JSON.parse(result);
    if (parsed.slides && Array.isArray(parsed.slides)) {
      return parsed as SlidesResult;
    }
    return null;
  } catch {
    return null;
  }
}

type UsePresentationChatOptions = {
  selectedSlideIndex: number;
};

export function usePresentationChat({ selectedSlideIndex }: UsePresentationChatOptions) {
  const { id: projectId } = useParams() as { id: string };
  const {
    getChatHistory,
    addMessage,
    clearChatHistory,
    removeLastMessage,
    markMessageActedOn,
    getLoadingState,
    setLoadingState,
    resetLoadingState,
    getMessagesWithPending
  } = usePresentationChatStore();
  const {
    setPresentation,
    setLocalPresentation,
    markClean,
    setSlidesUpdatedFromChat,
    setOutlineWasRefreshed
  } = usePresentationStore();
  const { updateSelectedProjectPresentation } = useProjectStore();

  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isAbortingRequest, setIsAbortingRequest] = useState(false);

  const selectedSlideIndexRef = useRef(selectedSlideIndex);
  selectedSlideIndexRef.current = selectedSlideIndex;

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const requestInFlightRef = useRef(false);
  const processedTasksRef = useRef<Set<number>>(new Set());
  const hasPolledOnceRef = useRef(false);

  const loadingState = getLoadingState(projectId);
  const { loadingMessageId, pendingGenerationId, generationStep } = loadingState;
  const isLoading = !!pendingGenerationId;

  const messages = getMessagesWithPending(projectId);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollGenerationStatus = useCallback(async () => {
    const currentLoadingState = getLoadingState(projectId);
    const currentPendingGenerationId = currentLoadingState.pendingGenerationId;

    if (requestInFlightRef.current || !currentPendingGenerationId) {
      return;
    }
    requestInFlightRef.current = true;

    try {
      const statuses = await getStatusAll(projectId, EGenerationTaskType.chatSlides);

      const slidesTask = statuses.find((status) => status.id === currentPendingGenerationId);

      if (!slidesTask || processedTasksRef.current.has(slidesTask.id)) {
        return;
      }

      switch (slidesTask.status) {
        case EGenerationStatus.pending:
        case EGenerationStatus.processing:
        case EGenerationStatus.streaming: {
          const step = parseGenerationStep(slidesTask.result);
          setLoadingState(projectId, { generationStep: step });
          break;
        }

        case EGenerationStatus.ready: {
          await confirmPollingEnd(slidesTask.id);
          processedTasksRef.current.add(slidesTask.id);

          const slidesResult = parseSlidesResult(slidesTask.result);

          let completionMessageText = "Done";
          if (slidesResult?.message) {
            completionMessageText = slidesResult.message;
          } else if (slidesTask.result) {
            try {
              const parsed = JSON.parse(slidesTask.result);
              if (parsed.message && typeof parsed.message === "string") {
                completionMessageText = parsed.message;
              }
            } catch {
              // ignore
            }
          }

          if (slidesResult && slidesResult.slides.length > 0) {
            const updatedSlides: TSlide[] = slidesResult.slides.map((slide) => ({
              id: String(slide.id),
              title: slide.title,
              outline: slide.content,
              order: slide.order
            }));

            const currentState = usePresentationStore.getState();
            const currentLocalPresentation = currentState.localPresentation || {
              id: 0,
              projectId: parseInt(projectId),
              status: "completed" as const,
              gammaStatus: null,
              gammaWebUrl: null,
              gammaPptxUrl: null,
              gammaPdfUrl: null,
              outlineGeneratedAt: new Date().toISOString(),
              outlineRefreshedAt: null,
              outlineUpdatedAt: new Date().toISOString(),
              gammaGeneratedAt: null,
              createdAt: new Date().toISOString(),
              allowRegeneration: false,
              slides: []
            };

            const updatedPresentation: TPresentation = {
              ...currentLocalPresentation,
              slides: updatedSlides,
              gammaStatus: null,
              gammaWebUrl: null,
              gammaPptxUrl: null,
              gammaPdfUrl: null,
              outlineGeneratedAt: new Date().toISOString(),
              outlineUpdatedAt: new Date().toISOString(),
              allowRegeneration: true
            };

            setSlidesUpdatedFromChat(true);
            setPresentation(updatedPresentation);
            setLocalPresentation(updatedPresentation);
            markClean();
            setOutlineWasRefreshed(true);

            updateSelectedProjectPresentation({
              status: "completed",
              gammaWebUrl: null,
              gammaPptxUrl: null,
              slidesCount: updatedSlides.length
            });

            setTimeout(() => {
              setSlidesUpdatedFromChat(false);
            }, 1000);

            toast.success("Slides updated successfully!");
          } else {
            // eslint-disable-next-line no-console
            console.warn("[PRESENTATION_CHAT] No slides found in task result:", slidesTask.result);
          }

          const currentLoadingState = getLoadingState(projectId);
          const completionMessage: PresentationChatMessage = {
            role: "assistant",
            content: completionMessageText,
            id: currentLoadingState.loadingMessageId || `bot-${Date.now()}`
          };
          addMessage(projectId, completionMessage);

          // Reset loading state
          resetLoadingState(projectId);
          stopPolling();
          break;
        }

        case EGenerationStatus.error: {
          await confirmPollingEnd(slidesTask.id);
          processedTasksRef.current.add(slidesTask.id);

          // Add error message
          const currentLoadingState = getLoadingState(projectId);
          const errorMessage: PresentationChatMessage = {
            role: "assistant",
            content: JSON.stringify({
              type: "error",
              content: "Generation failed. Please try again."
            }),
            id: currentLoadingState.loadingMessageId || `bot-${Date.now()}`
          };
          addMessage(projectId, errorMessage);

          resetLoadingState(projectId);
          stopPolling();
          break;
        }

        case EGenerationStatus.cancelled:
          processedTasksRef.current.add(slidesTask.id);
          resetLoadingState(projectId);
          stopPolling();
          break;
      }
    } catch (error) {
      console.error("Error polling generation status:", error);
    } finally {
      requestInFlightRef.current = false;
    }
  }, [
    projectId,
    getLoadingState,
    addMessage,
    stopPolling,
    resetLoadingState,
    setPresentation,
    setLocalPresentation,
    markClean,
    setSlidesUpdatedFromChat,
    updateSelectedProjectPresentation
  ]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    pollGenerationStatus();
    pollingIntervalRef.current = setInterval(pollGenerationStatus, 2000);
  }, [pollGenerationStatus]);

  // Poll once on mount to check for active tasks (similar to ChatIsland)
  const pollOnce = useCallback(async () => {
    if (hasPolledOnceRef.current) return;
    hasPolledOnceRef.current = true;

    try {
      const currentLoadingState = getLoadingState(projectId);

      // If we have a stored pendingGenerationId, check if the task is still active
      if (currentLoadingState.pendingGenerationId) {
        const statuses = await getStatusAll(projectId, EGenerationTaskType.chatSlides);
        const slidesTask = statuses.find((status) => status.id === currentLoadingState.pendingGenerationId);

        if (slidesTask) {
          // Restore pending bot message if we have loadingMessageId
          if (!currentLoadingState.pendingBotMessage && currentLoadingState.loadingMessageId) {
            setLoadingState(projectId, {
              pendingBotMessage: {
                role: "assistant",
                content: "",
                id: currentLoadingState.loadingMessageId
              }
            });
          }

          // If task is still active, start polling
          if (
            slidesTask.status === EGenerationStatus.pending ||
            slidesTask.status === EGenerationStatus.processing ||
            slidesTask.status === EGenerationStatus.streaming
          ) {
            startPolling();
          } else {
            // Task is completed/error/cancelled, process it immediately
            pollGenerationStatus();
          }
        } else {
          // Task not found, reset state
          resetLoadingState(projectId);
        }
      }
    } catch (error) {
      console.error("Error polling on mount:", error);
    }
  }, [projectId, getLoadingState, setLoadingState, resetLoadingState, startPolling, pollGenerationStatus]);

  // Poll once on mount
  useEffect(() => {
    pollOnce();
  }, [pollOnce]);

  // Start polling when we have a pending generation
  useEffect(() => {
    if (pendingGenerationId) {
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGenerationId]);

  const handleSubmit = async (messageText: string) => {
    if (!projectId || !messageText.trim() || isLoading) return;

    const userMessage: PresentationChatMessage = {
      role: "user",
      content: messageText,
      id: `user-${Date.now()}`
    };
    addMessage(projectId, userMessage);

    const botMessageId = `bot-${Date.now()}`;
    const botLoadingMessage: PresentationChatMessage = {
      role: "assistant",
      content: "",
      id: botMessageId
    };

    try {
      setIsSendingRequest(true);
      setLoadingState(projectId, {
        loadingMessageId: botMessageId,
        pendingBotMessage: botLoadingMessage,
        pendingGenerationId: null
      });

      const { isDirty, localPresentation: currentLocal } = usePresentationStore.getState();
      if (isDirty && currentLocal) {
        await $api.post(`/projects/${projectId}/presentations/save`, {
          slides: currentLocal.slides.map((slide, index) => ({
            title: slide.title,
            content: slide.outline,
            order: index + 1
          }))
        });
        setPresentation(currentLocal);
        markClean();
      }

      const messagesHistory = getLastItems(
        getChatHistory(projectId)
          .filter((m) => {
            try {
              return JSON.parse(m.content).type !== "notification";
            } catch {
              return true;
            }
          })
          .map((m) => ({ role: m.role, content: m.content })),
        MAX_CHAT_HISTORY_LENGTH
      );

      const response = await $api.post<{ generationId: number }>(`/projects/${projectId}/presentations/chat`, {
        content: messageText,
        messagesHistory,
        model: null,
        selectedSlideIndex: selectedSlideIndexRef.current
      });

      setLoadingState(projectId, {
        pendingGenerationId: response.data.generationId
      });
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error("Error starting generation:", error);

      const isReadOnly = error instanceof AxiosError && error.response?.status === 403;
      const messageContent = isReadOnly
        ? {
            type: "warning",
            content:
              "Sorry, you have read-only access and cannot make changes to the presentation. You can ask questions about the project, but editing is not available for your account."
          }
        : { type: "error", content: "Failed to send message. Please try again." };

      const errorMessage: PresentationChatMessage = {
        role: "assistant",
        content: JSON.stringify(messageContent),
        id: botMessageId
      };
      addMessage(projectId, errorMessage);

      resetLoadingState(projectId);
    } finally {
      setIsSendingRequest(false);
    }
  };

  const handleAbortRequest = async () => {
    const currentLoadingState = getLoadingState(projectId);
    if (!currentLoadingState.pendingGenerationId) return;

    setIsAbortingRequest(true);
    try {
      await $api.post("/chat/cancel", { generationId: currentLoadingState.pendingGenerationId });
      removeLastMessage(projectId);
    } catch (error) {
      console.error("Failed to cancel generation:", error);
    } finally {
      setIsAbortingRequest(false);
      resetLoadingState(projectId);
      stopPolling();
    }
  };

  const handleClearChat = () => {
    clearChatHistory(projectId);
  };

  const handleActionButton = (messageText: string, messageId: string) => {
    markMessageActedOn(projectId, messageId);
    handleSubmit(messageText);
  };

  return {
    messages,
    hasMessages: getChatHistory(projectId).length > 0,
    isLoading,
    isSendingRequest,
    isAbortingRequest,
    loadingMessageId,
    generationStep,
    handleSubmit,
    handleAbortRequest,
    handleClearChat,
    handleActionButton
  };
}
